import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../utils/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { Alert } from "@mui/material";
import BreadcrumbNav from "../components/BreadcrumbNav";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
import CustomDatePicker from "../components/CustomDatePicker";
import {
  Container,
  Card,
  CardContent,
  Stack,
  Breadcrumbs,
  Link,
  Autocomplete,
  FormControl,
  Box,
  Grid,
  MenuItem,
  InputAdornment,
  Select,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { fetchFiles } from "../redux/actions/fileActions";
import { getTimesheetsByCaseId } from "../redux/actions/timesheetActions";
import { getFile } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import {
  createTimesheet,
  listTimesheets,
} from "../redux/actions/timesheetActions";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { textFieldStyles } from "../styles/textFieldStyles";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import EuroIcon from "@mui/icons-material/Euro";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { setNotifications } from "../redux/actions/notificationActions";

const tableStyles = windowsTableStyles();

const RATES = {
  "working_time": 100,
  "travel_time": 100,
  kilometers: 0.21,
};

// Breadcrumbs will be set in component with translation

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const windowsInputStyles = {
  "& .MuiInputBase-root": {
    fontFamily: "'Segoe UI', sans-serif",
    fontSize: "14px",
  },
  "& .MuiSelect-select": {
    padding: "4px 8px",
  },
  "& .MuiCheckbox-root": {
    padding: "4px",
  },
};

const CreateTimesheetPage = () => {
  const { t } = useTranslation(['pages', 'forms', 'buttons', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // success, error, warning, info
  const { timesheets } = useSelector((state) => state.timesheetList);
  const { file } = useSelector((state) => state.fileDetails);
  const [id, setId] = useState(location.state?.caseId);
  const files = useSelector((state) => state.fileList.files || []);
  const relationships = useSelector(
    (state) => state.relationshipList.relationships || []
  );
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [ownerHourlyRate, setOwnerHourlyRate] = useState(0);

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user) {
      console.log("User...", user);
      setUserRole(user.role);
    }
  }, [user]);

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('navigation:home'), href: "/" },
    { label: t('navigation:timesheets'), href: "/timesheets" },
  ]);

  const [formData, setFormData] = useState(() => {
    // Extract case ID from state or search params
    const params = new URLSearchParams(location.search);
    const queryCaseId = params.get('caseId');
    const locationCaseId = queryCaseId || location.state?.caseId;
    
    // Extract date from URL params if present
    const dateParam = params.get('date');
    
    const initialCaseFile =
      files?.find((file) => file._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t('common:case_file'),
          href: `/files/${initialCaseFile._id}/timesheets`,
        },
      ]);
    }
    return {
      owner: "",
      type: "",
      description: "",
      date: dateParam || getCurrentDate(), // Use date from URL or current date
      case: locationCaseId,
    };
  });

  useEffect(() => {
    if (user) {
      setFormData({ ...formData, owner: user.relationship });
      setOwnerHourlyRate(user.hourlyRate);
      // console.log("User...", ownerHourlyRate);
    }
  }, [user]);

  useEffect(() => {
    if (formData.case) {
      const selectedFile = files.find(file => file._id === formData.case);
      if (selectedFile) {
        const { rateConfig } = selectedFile;
        if (rateConfig.type === 'hourly') {
          setOwnerHourlyRate(rateConfig.hourlyRate);
          // Update all existing billing items with new rate
          setBillingItems(prevItems => prevItems.map(item => ({
            ...item,
            rate: rateConfig.hourlyRate
          })));
        } else {
          setOwnerHourlyRate(0);
          setBillingItems(prevItems => prevItems.map(item => ({
            ...item,
            rate: 0
          })));
        }
      }
    }
  }, [formData.case, files]);

  const [totalDuration, setTotalDuration] = useState(0);
  const [totalDurationDisplay, setTotalDurationDisplay] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [billingItems, setBillingItems] = useState([
    {
      type: "working_time", // Default value
      description: "", // Default value
      rate: ownerHourlyRate, // Default value
      quantity: "00:00:00", // Default value
      billable: true, // Default value
    },
  ]);

  const billingItemTypes = [
    { value: "working_time", label: t('pages:timesheets.working_time') },
    { value: "travel_time", label: t('pages:timesheets.travel_time') },
    { value: "kilometers", label: t('pages:timesheets.kilometers') }
  ];
  const predefinedQuantities = [
    "00:00:00",
    "00:06:00",
    "00:12:00",
    "00:18:00",
    "00:24:00",
    "00:30:00",
    "00:36:00",
    "00:42:00",
    "00:48:00",
    "00:54:00",
    "01:00:00",
    "01:06:00",
    "01:12:00",
    "01:18:00",
    "01:24:00",
    "01:30:00",
    "01:36:00",
    "01:42:00",
    "01:48:00",
    "01:54:00",
    "02:00:00",
    "02:06:00",
    "02:12:00",
    "02:18:00",
    "02:24:00",
    "02:30:00",
    "02:36:00",
    "02:42:00",
    "02:48:00",
    "02:54:00",
    "03:00:00",
  ];
  useEffect(() => {
    const durationSum = billingItems.reduce((sum, item) => {
      if (item.type !== "kilometers") {
        const [hours, minutes, seconds] = item.quantity.split(":").map(Number);
        const decimalHours = hours + minutes / 60 + seconds / 3600; // Convert to decimal hours
        return sum + decimalHours;
      }
      return sum;
    }, 0);

    // Convert decimal hours to hours, minutes, and seconds
    const totalHours = Math.floor(durationSum);
    const totalHours2Digit = Math.floor(durationSum)
      .toString()
      .padStart(2, "0");
    const totalMinutes = Math.floor((durationSum - totalHours) * 60);
    const totalSeconds = Math.round(
      ((durationSum - totalHours) * 60 - totalMinutes) * 60
    );
    const totalMinutes2Digit = Math.floor((durationSum - totalHours) * 60)
      .toString()
      .padStart(2, "0");

    const totalSeconds2Digit = Math.round(
      ((durationSum - totalHours) * 60 - totalMinutes) * 60
    )
      .toString()
      .padStart(2, "0");
    const totalDurationDigitsOnly = `${totalHours2Digit}:${totalMinutes2Digit}:${totalSeconds2Digit}`;
    const formattedDuration = `${totalHours}h: ${totalMinutes}m: ${totalSeconds}s`;
    setTotalDurationDisplay(formattedDuration);
    setTotalDuration(totalDurationDigitsOnly); // Set the total duration in hours, minutes, and seconds format

    let total = 0;
    billingItems.forEach((item) => {
      if (item.type !== "kilometers") {
        const [hours, minutes, seconds] = item.quantity.split(":").map(Number);
        const decimalHours = hours + minutes / 60 + seconds / 3600; // Convert to decimal hours
        total += decimalHours * parseFloat(item.rate);
      } else {
        const amount = parseFloat(item.quantity) * parseFloat(item.rate);
        total += amount;
      }
    });
    setTotalAmount(total.toFixed(2)); // Set the total amount with 2 decimal places
  }, [billingItems]);

  useEffect(() => {
    // console.log("Owner...", location.state?.owner);
    dispatch(fetchFiles());
    dispatch(fetchRelationships());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getFile(id));
  }, [dispatch, id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOwnerChange = async (event, newValue) => {
    const ownerId = newValue?._id || "";
    setFormData({ ...formData, owner: ownerId });

    if (ownerId) {
      try {
        const response = await axios.get(`/api/relationships/${ownerId}`);
        const owner = response.data;
        if (owner.contractType === "Individual") {
          setOwnerHourlyRate(owner.hourlyRate); // Set the owner's hourly rate

          setBillingItems([
            {
              type: "working_time",
              description: "",
              rate: owner?.hourlyRate,
              quantity: "00:00:00",
              billable: true,
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching owner details:", error);
      }
    }
  };

  const handleBillingItemChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedItems = billingItems.map((item, i) => {
      if (i === index) {
        if (
          name === "quantity" &&
          (item.type === "working_time" || item.type === "travel_time")
        ) {
          let newValue = value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
          if (newValue.length > 4) {
            newValue =
              newValue.slice(0, 2) +
              ":" +
              newValue.slice(2, 4) +
              ":" +
              newValue.slice(4, 6);
          } else if (newValue.length > 2) {
            newValue = newValue.slice(0, 2) + ":" + newValue.slice(2, 4);
          }
          return { ...item, [name]: newValue };
        }
        return { ...item, [name]: type === "checkbox" ? checked : value };
      }
      return item;
    });
    setBillingItems(updatedItems);
  };

  const handleDeleteBillingItem = (index) => {
    const updatedItems = billingItems.filter((_, i) => i !== index);
    setBillingItems(updatedItems);
  };

  const handleAddBillingItem = () => {
    const currentFile = files.find(f => f._id === formData.case);
    const rate = currentFile?.rateConfig?.type === 'hourly' ? currentFile.rateConfig.hourlyRate : 0;

    setBillingItems([
      ...billingItems,
      {
        type: "working_time",
        description: "",
        rate: rate,
        quantity: "00:00:00",
        billable: true,
      },
    ]);
  };

  const activityTypes = [
    { label: "COCL - Correspondentie aan/van cliënt" },
    { label: "COVE - Correspondentie aan/van verzekeraar" },
    { label: "COSC - Correspondentie aan/van schaderegelaar" },
    { label: "COTE - Correspondentie aan/van (advocaat) tegenpartij" },
    { label: "COON - Correspondentie aan/van (toedrachts)onderzoeker" },
    { label: "COWE - Correspondentie aan/van werkgever" },
    { label: "COEX - Correspondentie aan/van (schade)expert" },
    { label: "COFT - Correspondentie aan/van fysiotherapeut" },
    { label: "COPO - Correspondentie aan/van Politie / OM" },
    { label: "COFI - Correspondentie aan/van (financieel) deskundige" },
    { label: "COAR - Correspondentie aan/van arbeidsdeskundige" },
    { label: "COAI - Correspondentie aan/van arbeidsinspectie" },
    { label: "COBE - Correspondentie aan/van bestuursorgaan" },
    { label: "COBO - Correspondentie aan/van boekhouder" },
    { label: "COMATGP - Correspondentie aan/van medisch adviseur tegenpartij" },
    { label: "COMA - Correspondentie aan/van medisch adviseur" },
    { label: "COZO - Correspondentie aan/van zorgverzekeraar" },
    { label: "CODE - Correspondentie aan/van derde" },
    { label: "COHU - Correspondentie aan/van huisarts" },
    { label: "COZIE - Correspondentie aan/van ziekenhuis" },
    { label: "COPS - Correspondentie aan/van psycholoog" },
    { label: "CORE - Correspondentie aan/van revalidatiearts" },
    { label: "COVER - Correspondentie aan/van verpleeghuis" },
    { label: "COBED - Correspondentie aan/van bedrijfsarts" },
    { label: "COOR - Correspondentie aan/van orthopeed" },
    { label: "COER - Correspondentie aan/van ergotherapeut" },
    { label: "COTA - Correspondentie aan/van tandarts" },
    { label: "COG - Correspondentie aan/van gemeente" },
    { label: "COU - Correspondentie aan/van UWV" },
    { label: "CORE - Correspondentie aan/van rechterlijke macht" },
    { label: "CODE - Correspondentie aan/van deurwaarder" },
    { label: "TECL - Telefoongesprek met cliënt" },
    { label: "TEMA - Telefoongesprek met medisch adviseur" },
    { label: "TETE - Telefoongesprek met (advocaat) tegenpartij" },
    { label: "TEMATGP - Telefoongesprek met medisch adviseur tegenpartij" },
    { label: "TEUW - Telefoongesprek met UWV" },
    { label: "TEGE - Telefoongesprek met gemeente" },
    { label: "TEZIE - Telefoongesprek met ziekenhuis" },
    { label: "TEDE - Telefoongesprek met derde" },
    { label: "TEARINS - Telefoongesprek met arbeidsinspectie" },
    { label: "TEHU - Telefoongesprek met huisarts" },
    { label: "TEREVA - Telefoongesprek met revalidatiearts" },
    { label: "TEPSY - Telefoongesprek met psycholoog" },
    { label: "TESC - Telefoongesprek met schaderegelaar" },
    { label: "TEZO - Telefoongesprek met zorgverzekeraar" },
    { label: "TEBOE - Telefoongesprek met boekhouder" },
    { label: "TEERG - Telefoongesprek met ergotherapeut" },
    { label: "TEPO - Telefoongesprek met Politie / OM" },
    { label: "TEORT - Telefoongesprek met orthopeed" },
    { label: "TEVRP - Telefoongesprek met verpleeghuis" },
    { label: "TEEX - Telefoongesprek met (schade)expert" },
    { label: "TETA - Telefoongesprek met tandarts" },
    { label: "TEFYS - Telefoongesprek met fysiotherapeut" },
    { label: "TEWE - Telefoongesprek met werkgever" },
    { label: "TEBED - Telefoongesprek met bedrijfsarts" },
    { label: "TEOND - Telefoongesprek met (toedrachts)onderzoeker" },
    { label: "TEBE - Telefoongesprek met bestuursorgaan" },
    { label: "TEVE - Telefoongesprek met verzekeraar" },
    { label: "TEFI - Telefoongesprek met financieel deskundige" },
    { label: "TEARDE - Telefoongesprek met arbeidsdeskundige" },
    { label: "TERE - Telefoongesprek met rechterlijke macht" },
    { label: "TEDWR - Telefoongesprek met deurwaarder" },
    { label: "TERDW - Telefoongesprek met RDW" },
    { label: "BEKA - Bespreking met cliënt op kantoor" },
    { label: "BELO - Bespreking bij cliënt op locatie" },
    { label: "GEBECL - Gezamenlijk bezoek bij cliënt" },
    { label: "GEBEKA - Gezamenlijk bezoek op kantoor" },
    { label: "BECLE - Bespreking met cliënt elders" },
    { label: "BEDEE - Bespreking met derde elders" },
    { label: "BEDEKA - Bespreking met derde op kantoor" },
    { label: "VOBE - Voorbereiding van bespreking" },
    { label: "STUDLI - Studie literatuur en jurisprudentie" },
    { label: "STUO - Studie ontvangen stuk(ken)" },
    { label: "STUPRO - Studie ontvangen processtuk" },
    { label: "STUDIEDOS - Studie dossier" },
    { label: "DAG - (Ontwerp) dagvaarding" },
    { label: "VERZ - (Ontwerp) verzoek- of beroepschrift" },
    { label: "CONCL - (Ontwerp) conclusie, memorie, akte, verweerschrift" },
    { label: "PLEITNOTA - (Ontwerp) pleitnota" },
    { label: "OVK - (Ontwerp) overeenkomst" },
    { label: "PRODUC - Verzamelen / concipiëren bewijsstukken / producties" },
    { label: "VOORBER - (Voorbereiding) andere verrichtingen in rechte" },
    { label: "PLEID - Pleidooi / mondelinge behandeling" },
    { label: "ENQU - Bijwonen enquete" },
    { label: "COMP - Bijwonen comparitie" },
    { label: "ZITTING - Bijwonen (hoor)zitting" },
    { label: "BEZICHT - Bezichtiging ter plaatse" },
    { label: "REISCL - Reizen voor cliënt" },
    { label: "REISA - Reizen algemeen" },
    { label: "DIV - Diverse werkzaamheden" },
    { label: "FEITOND - (Feiten) onderzoek (internet, KvK, e.d.)" },
    { label: "OPPV - Opvragen proces-verbaal" },
    { label: "SCHADE - Opstellen schadestaat" },
    { label: "ALG - Algemeen" },
  ];

  const commonTextFieldStyles = {
    "& .MuiInputBase-root": {
      border: "none",
      padding: "0 8px", // Add small padding
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)", // Light grey background on hover
      },
    },
    "& .MuiInputLabel-root": {
      "&:hover": {
        color: "primary.main", // Change label color on hover
      },
    },
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const { loading, success, error } = useSelector(
    (state) => state.timesheetCreate
  );

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  function parseDurationToHours(timeString) {
    const [hh, mm, ss] = timeString.split(":").map(Number);
    return hh + mm / 60 + ss / 3600;
  }

  useEffect(() => {
    const runAsync = async () => {
      if (success) {
        setNotification({
          open: true,
          message: t('pages:timesheets.created_successfully'),
          severity: "success",
        });
        dispatch({ type: "TIMESHEET_CREATE_RESET" }); // Reset the success state
        dispatch(fetchFiles());
        // console.log("id ", id);
        dispatch(getFile(id));

        dispatch(getTimesheetsByCaseId(id));
        // Then compare sums with file's alarm settings

        setTimeout(() => { }, 2000);
        const sumHours = timesheets?.reduce((acc, ts) => {
          return acc + parseDurationToHours(ts.totalDuration);
        }, 0);
        const sumFees = timesheets?.reduce(
          (acc, ts) => acc + ts.totalAmount,
          0
        );
        console.log(sumHours, sumFees);
        const hourLimit = files.find(
          (file) => file._id === formData.case
        )?.signalingHours;
        const feeLimit = files.find(
          (file) => file._id === formData.case
        )?.limitOnFees;
        const percent =
          files.find((file) => file._id === formData.case)?.warningThreshold /
          100;
        let showNotification = false;
        console.log(
          "File: ",
          hourLimit,
          feeLimit,
          percent,
          hourLimit * (1 * percent),
          feeLimit * (1 * percent)
        );
        if (
          !files.find((file) => file._id === formData.case).notificationShown
        ) {
          if (
            sumHours >= hourLimit * (1 * percent) ||
            sumFees >= feeLimit * (1 * percent)
          ) {
            showNotification = true;
          }
        }
        console.log("showNotification ", showNotification);
        if (showNotification) {
          // console.log("Show notification");
          // Trigger UI notification
          // alert("Warning: You have exceeded the configured limits.");
          // Mark notification as shown in the DB
          await axios.put(`/api/files/${id}`, { notificationShown: true });
          await axios.post("/api/notifications", {
            user: user?.relationship,
            type: "File",
            subject: t('pages:timesheets.work_limit_exceeded'),
            file: id,
          });
          dispatch(getFile(id)); // refresh store
          const res = await axios.get(`/api/notifications/${user._id}`);
          setTimeout(() => { }, 2000);
          dispatch(setNotifications(res.data));
        }

        setTimeout(() => {
          navigate(-1);
        }, 1000);

        // window.location.reload(true);
        // navigate('/documents');
      } else if (error) {
        setNotification({
          open: true,
          message: error,
          severity: "error",
        });
        dispatch({ type: "TIMESHEET_CREATE_RESET" }); // Reset the error state
      }
    };
    runAsync();
  }, [success, error, dispatch]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(formData.case);

    // Validate that no non-kilometer billing item has zero time
    const hasZeroTimeItem = billingItems.some(
      (item) =>
        item.type !== "kilometers" &&
        (item.quantity === "00:00:00" || !item.quantity)
    );

    if (hasZeroTimeItem) {
      setNotification({
        open: true,
        message: t('pages:timesheets.time_zero_error'),
        severity: "error",
      });
      return;
    }

    setId(formData.case);
    dispatch(
      createTimesheet({ formData, billingItems, totalDuration, totalAmount })
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (userRole === "Manager") {
      return true; // Managers can see all files
    } else if (userRole === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        // console.log('Member ID:', member.member._id);
        // console.log('User Relationship ID:', user.relationship);
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (userRole === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const inputStyles = { width: "100%", marginBottom: 2 };
  const buttonStyles = { margin: "0 8px" };

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:timesheets.create_title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl
                variant="standard"
                sx={{ mr: 3, width: "100%" }}
                required
              >
                <CustomAutocomplete
                  options={relationships}
                  value={
                    relationships.find((rel) => rel._id === formData.owner) ||
                    null
                  }
                  onChange={handleOwnerChange}
                  label={t('common:owner')}
                  filterType="Member"
                  addNewLabel={t('pages:timesheets.add_owner')}
                  addNewPath="/relationships/create"
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={activityTypes}
                getOptionLabel={(option) => option.label}
                onChange={(event, newValue) =>
                  handleChange({
                    target: { name: "type", value: newValue?.label || "" },
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('common:code')}
                    fullWidth
                    sx={textFieldStyles({ width: "100%" })}
                    size="small"
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <Box sx={{ width: "100%" }}>
                <CustomDatePicker
                  label={t('common:date')}
                  value={formData.date}
                  onChange={(value) =>
                    handleChange({
                      target: {
                        name: "date",
                        value: value,
                      },
                    })
                  }
                  required
                  sx={{ width: "100%" }}
                />
              </Box>
            </Grid>

            <Grid item xs={4}>
              <FileAutocomplete
                options={filteredFiles}
                value={files.find((file) => file._id === formData.case) || null}
                onChange={(event, newValue) =>
                  handleChange({
                    target: { name: "case", value: newValue?._id || "" },
                  })
                }
                label={t('common:case')}
                name="case"
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label={t('common:description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                fullWidth
                sx={textFieldStyles()}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <div>
                <Typography variant="h6" sx={tableStyles.typography}>
                  {t('pages:timesheets.billing_items')}
                </Typography>
                <TableContainer component={Paper} sx={tableStyles.container}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableStyles.headerCell}>{t('common:type')}</TableCell>
                        <TableCell sx={tableStyles.headerCell}>{t('common:rate')}</TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            width: "200px",
                          }}
                        >
                          {t('common:quantity')}
                        </TableCell>
                        {/* <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            width: "100px",
                          }}
                        >
                          {t('common:status')}
                        </TableCell> */}
                        <TableCell
                          sx={{
                            ...tableStyles.headerCell,
                            width: "100px",
                          }}
                        >
                          {t('common:amount')}
                        </TableCell>
                        <TableCell sx={tableStyles.headerCell}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billingItems.map((item, index) => (
                        <TableRow key={index} sx={tableStyles.row}>
                          <TableCell sx={tableStyles.cell}>
                            <TextField
                              select
                              variant="standard"
                              name="type"
                              required
                              value={item.type}
                              onChange={(e) => {
                                handleBillingItemChange(index, e);
                                // Update quantity based on type
                                const newQuantity =
                                  e.target.value === "kilometers"
                                    ? "0"
                                    : "00:00:00";
                                const updatedItems = [...billingItems];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  type: e.target.value,
                                  quantity: newQuantity,
                                };
                                // console.log(updatedItems);
                                setBillingItems(updatedItems);
                              }}
                              fullWidth
                              InputProps={{
                                disableUnderline: true,
                              }}
                              sx={{ ...commonTextFieldStyles, minWidth: 150 }}
                            >
                              {billingItemTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          {/* <TableCell
                          sx={{ borderBottom: "none", padding: "4px 8px" }}
                        >
                          <TextField
                            variant="standard"
                            name="description"
                            value={item.description}
                            onChange={(e) => handleBillingItemChange(index, e)}
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                            }}
                            sx={commonTextFieldStyles}
                          />
                        </TableCell> */}
                          <TableCell sx={tableStyles.cell}>
                            <TextField
                              variant="standard"
                              name="rate"
                              type="number"
                              required
                              value={item.rate}
                              onChange={(e) =>
                                handleBillingItemChange(index, e)
                              }
                              disabled={files.find(f => f._id === formData.case)?.rateConfig?.type !== 'hourly'}
                              fullWidth
                              InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography variant="body2">€</Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={commonTextFieldStyles}
                            />
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <TextField
                              variant="standard"
                              name="quantity"
                              required
                              value={item.quantity}
                              onChange={(e) =>
                                handleBillingItemChange(index, e)
                              }
                              fullWidth
                              InputProps={{
                                disableUnderline: true,
                                endAdornment:
                                  item.type !== "kilometers" ? (
                                    <InputAdornment position="end">
                                      <Select
                                        value=""
                                        onChange={(e) =>
                                          handleBillingItemChange(index, {
                                            target: {
                                              name: "quantity",
                                              value: e.target.value,
                                            },
                                          })
                                        }
                                        displayEmpty
                                        IconComponent={ArrowDropDownIcon}
                                        sx={{
                                          "& .MuiSelect-select": {
                                            padding: 0,
                                            display: "flex",
                                            alignItems: "center",
                                          },
                                          "& .MuiOutlinedInput-notchedOutline":
                                          {
                                            border: "none",
                                          },
                                          "& .MuiPaper-root": {
                                            width: "100%",
                                          },
                                        }}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: {
                                              maxHeight: 200, // Set the maximum height for the dropdown menu
                                              overflowY: 'auto', // Enable vertical scrolling
                                              "&::-webkit-scrollbar": {
                                                width: "8px", // Decrease the width of the scrollbar
                                              },
                                              "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#888", // Color of the scrollbar thumb
                                                borderRadius: "4px", // Rounded corners for the scrollbar thumb
                                              },
                                              "&::-webkit-scrollbar-thumb:hover": {
                                                backgroundColor: "#555", // Color of the scrollbar thumb on hover
                                              },
                                            },
                                          },
                                        }}
                                      >
                                        {predefinedQuantities.map(
                                          (quantity) => (
                                            <MenuItem
                                              key={quantity}
                                              value={quantity}
                                            >
                                              {quantity}
                                            </MenuItem>
                                          )
                                        )}
                                      </Select>
                                    </InputAdornment>
                                  ) : null,
                              }}
                              placeholder={
                                item.type === "kilometers"
                                  ? "0 Kilometers"
                                  : "hh:mm:ss"
                              }
                              sx={commonTextFieldStyles}
                            />
                          </TableCell>
                          {/* <TableCell sx={tableStyles.cell}>
                            
                            <FormGroup>
                              <FormControlLabel
                                label={t('common:billable')}
                                control={
                                  <Checkbox
                                    name="billable"
                                    checked={item.billable}
                                    onChange={(e) =>
                                      handleBillingItemChange(index, e)
                                    }
                                  />
                                }
                              />
                            </FormGroup>
                          </TableCell> */}

                          <TableCell sx={tableStyles.cell}>
                            <Typography>
                              €{" "}
                              {item.type !== "kilometers"
                                ? (
                                  parseFloat(item.rate) *
                                  (parseInt(item.quantity.split(":")[0]) + // Hours
                                    parseInt(item.quantity.split(":")[1]) /
                                    60 + // Minutes
                                    parseInt(item.quantity.split(":")[2]) /
                                    3600)
                                ) // Seconds
                                  .toFixed(2)
                                  .toString()
                                  .replace(".", ",") // Replace decimal point with a comma
                                : (parseFloat(item.rate) *
                                  parseFloat(item.quantity) || 0)
                                  .toFixed(2)
                                  .toString()
                                  .replace(".", ",")
                              }
                            </Typography>
                            {/* <TextField
                    variant="standard"
                    name="rate"
                    type="number"
                    value={item.rate}
                    onChange={(e) => handleBillingItemChange(index, e)}
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                    }}
                    sx={commonTextFieldStyles}
                  /> */}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <IconButton
                              onClick={() => handleDeleteBillingItem(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddBillingItem}
                sx={{ marginTop: 2, textTransform: "none" }}
              >
                {t('pages:timesheets.add_billing_item')}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-end",
                }}
              >
                <Card
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "5px",
                    boxShadow: "none",
                    border: "none",
                  }}
                  elevation={0}
                >
                  <CardContent sx={{ padding: "8px" }}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: "bold" }}
                    >
                      {t('pages:timesheets.total_duration')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "1.2rem" }}
                    >
                      {totalDurationDisplay}
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "5px",
                    boxShadow: "none",
                    border: "none",
                  }}
                  elevation={0}
                >
                  <CardContent sx={{ padding: "8px" }}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: "bold" }}
                    >
                      {t('pages:timesheets.total_amount')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "1.2rem" }}
                    >
                      € {totalAmount.toString().replace(".", ",")}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              // padding: "10px 16px",
              // background: "#fff",
              zIndex: 10,
              backgroundColor: "white",
              color: "#000000",
              padding: "13px 80px",
            }}
          >
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              size="small"
            >
              {t('buttons:secondary.cancel')}
            </CancelButton>
            <SubmitButton onClick={handleSubmit} size="small">
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : t('pages:timesheets.create_button')
            }
            </SubmitButton>
          </Box>
        </form>
      </Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateTimesheetPage;
